import { FC, Fragment, useContext, useEffect, useState } from 'react';
import { AvailableBoardType, Hotel } from 'ts/hotel/interfaces/HotelResponse';
import {
  FilterHotelModel,
  FilterModel,
  LocationValue,
  RoomType,
} from 'ts/hotel/classes/FilterModel';
import { HotelFilterContext } from 'contexts/HotelFilterContext';
import FilterResultStars from './FilterResultStars';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBahtSign,
  faCrown,
  faExclamationTriangle,
  faFilter,
  faFilterCircleXmark,
  faK,
  faList,
  faLock,
  faMapLocationDot,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { onClickClose, onClickOverlay } from 'utils/responsive';
import { MapHotelContext } from 'contexts/MapHotelContext';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { useTranslation } from 'react-i18next';
import { currenciesList, getSortHotelOptions, getPriceModesList, flyeastCompanyId } from 'constants/hotel';
import { getKey, getRoomTypesArr, saveHotelDisplaySettings } from 'utils/hotel';
import { getHotelDisplayModeSettings } from 'constants/constant';
import Dropdown from 'components/shared/Dropdown';
import DisplayModeConfig from 'components/shared/DisplayModeConfig';
import FontAwesomeLayered from 'components/shared/ui/FontAwesomeLayered';
import SortBy from 'components/shared/SortBy';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { faAccessibleIcon } from '@fortawesome/free-brands-svg-icons';
import FilterSupplier from './FilterSupplier';
import FilterResultLeft from './FilterResultLeft';
import FilterResultLocation from './FilterResultLocation';
import { useSearchParams } from 'react-router-dom';
import { applyPollSupplierCounts } from 'services/hotel';
import { MainContext } from 'contexts/MainContext';
import { HotelConfigContext } from 'contexts/HotelConfigContext';
import { b2b, isFreeCxlDefault } from 'constants/generic';

interface FilterResultsProps {
  hotels?: Hotel[];
  onFilterChange: (filter: FilterModel) => void;
  onSortChange: (sort: string) => void;
  onLocationChange: (location: LocationValue) => void;
  totalHotelsCount: number;
  availableBoardTypes: AvailableBoardType[];
  dynamic?: {
    currency: string;
    choosenRoomPrice: number;
    numberOfPax: number;
  };
  loadingSearch?: boolean;
  sortBy: string;
}

const FilterResults: FC<FilterResultsProps> = (props) => {
  const { filter } = useContext(HotelFilterContext);
  const { hotelIndex } = useContext(HotelConfigContext);
  const { user, userSiteConfig, setUserSiteConfig, initSiteData } = useContext(MainContext);
  const [hasFilterValue, setHasFilterValue] = useState(false);
  const { setInitedMap, setCurrency, currency, priceMode, setPriceMode, activeTab, setActiveTab } =
    useContext(MapHotelContext);
  const [t] = useTranslation();
  const accessibilityOptions = filter?.roomTypes.find((it) => it.title === RoomType.ada)?.options;
  const [urlParams] = useSearchParams();
  const [currencyState, setCurrencyState] = useState(currenciesList);

  useEffect(() => {
    if (initSiteData?.companyId === flyeastCompanyId && urlParams.get('id') === 'TH') {
      setCurrencyState(
        currenciesList.concat({
          value: 'thb',
          label: '',
          icon: {
            path: faBahtSign,
            className: '',
          },
        })
      );

      setCurrency('thb');
      saveHotelDisplaySettings({ priceMode, currency: 'thb' });
      sessionStorage.setItem('displayCurrencyHotels', 'thb');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const newHasFilterValue =
      filter?.name ||
      filter?.room ||
      filter?.price?.value[0] !== filter?.price.min ||
      filter?.price?.value[1] !== filter?.price.max ||
      filter?.stars?.value.length ||
      filter?.location?.value.lng ||
      filter?.location?.km ||
      filter?.hType?.value.length ||
      filter?.additional?.value.length ||
      filter?.suppliers.some((sup) => sup.checked) ||
      filter?.meal.value?.length ||
      filter?.roomTypes.find((it) => it.options.some((opt) => opt.checked)) ||
      filter?.bedFilter?.include.length ||
      filter?.bedFilter?.exclude.length ||
      filter?.facilityFilter?.include.length;;

    setHasFilterValue(!!newHasFilterValue);
  }, [filter]);

  const clearFilters = () => {
    if (props.dynamic) {
      props.onFilterChange(
        new FilterHotelModel(
          props.hotels || [],
          props.dynamic.choosenRoomPrice,
          props.dynamic.numberOfPax,
          currency,
          !!isFreeCxlDefault
        )
      );
    } else {

      props.onFilterChange(
        applyPollSupplierCounts(
          new FilterModel(
            props.hotels || [],
            props.availableBoardTypes,
            currency,
            !!isFreeCxlDefault,
            hotelIndex
          )
        )
      );
    }

    setTimeout(() => {
      sessionStorage.removeItem(getKey('filterHotels', hotelIndex));
    }, 500);
  };

  const onChangeCurrency = (newCurrency: string) => {
    setCurrency(newCurrency);
    saveHotelDisplaySettings({ priceMode, currency: newCurrency }, hotelIndex);
  };

  const onChangePriceMode = (mode: string) => {
    setPriceMode(mode);
    saveHotelDisplaySettings({ priceMode: mode, currency }, hotelIndex);
  };

  const onChangeDisplayMode = (value: number) => {
    if ((userSiteConfig.hotel.displayMode || 7) === value) {
      return;
    }

    const settings = getHotelDisplayModeSettings(value);
    if (!settings) {
      return;
    }

    const nextConfig = {
      ...userSiteConfig,
      hotel: {
        ...userSiteConfig.hotel,
        ...settings,
      },
    };

    setUserSiteConfig(nextConfig);

    try {
      const userLocal = JSON.parse(localStorage.getItem('user') || '{}');
      userLocal.config = nextConfig;
      localStorage.setItem('user', JSON.stringify(userLocal));
    } catch {
      // Config still applies for this session without localStorage.
    }
  };

  const onClickAccessibility = () => {
    const newFilter = { ...filter! };

    if (newFilter.roomTypes) {
      const roomTypeIndex = newFilter.roomTypes.findIndex((it) => it.title === RoomType.ada);

      const isAdaChecked = newFilter.roomTypes[roomTypeIndex].options.every((opt) => opt.checked);

      const newRoomTypes = [...newFilter.roomTypes];
      newRoomTypes[roomTypeIndex] = {
        ...newRoomTypes[roomTypeIndex],
        options: newRoomTypes[roomTypeIndex].options.map((opt) => {
          return { ...opt, checked: !isAdaChecked };
        }),
      };

      const result = {
        ...newFilter,
        roomTypes: newRoomTypes,
        roomTypesArr: getRoomTypesArr(newRoomTypes),
        additional: { ...newFilter.additional, ada: !isAdaChecked },
      };
      props.onFilterChange(result);
    }
  };

  return (
    <div className="hotel-result-filter">
      {!!filter && (
        <div className="hotel-result-filter-row" onClick={onClickOverlay} style={{ zIndex: 23 }}>
          <div className="search-inner">
            <div className="results-responsive-header">
              <span className="title text-md">Filters</span>
              <FontAwesomeIcon icon={faTimes} onClick={onClickClose} />
            </div>
            <div className="hotel-display-mode-body">
              <Tooltip
                position="top"
                content={t('hotel.display_mode')}
                children={
                  <Dropdown
                    byClick
                    openerEmpty
                    appendToBody
                    align="left"
                    dropdownClassName="hotel-display-mode-dropdown"
                    opener={<Button title="" icon={faList} />}
                  >
                    <div className="dp-wrapper-sm text-lh" style={{ width: 280 }}>
                      <div className="mb-20">{t('hotel.display_mode')}</div>
                      <DisplayModeConfig
                        showTitle={false}
                        value={userSiteConfig.hotel.displayMode || 7}
                        onChange={onChangeDisplayMode}
                      />
                    </div>
                  </Dropdown>
                }
              />
            </div>
            <div className='ml-3 h100p'>
              <Tooltip
                position="top"
                content={t('map.map')}
                children={
                  <Button
                    title=""
                    icon={faMapLocationDot}
                    className={'btn btn-default h100p' + (activeTab === 0 ? ' btn-active-blue' : '')}
                    onClick={() => setActiveTab(activeTab === 0 ? 6 : 0)}
                  />
                }
              />
            </div>
            <SortBy
              onSortChange={props.onSortChange}
              options={getSortHotelOptions(t)}
              newSort={props.sortBy}
              hidePlaceholder={true}
              showTooltip
            />
            {!!filter?.roomTypes.length && (
              <Fragment>
                {!!accessibilityOptions?.length ? (
                  <Tooltip
                    position="top"
                    content={t('accessibility') + ' (' + accessibilityOptions[0].count + ')'}
                    children={
                      <Checkbox
                        className={
                          'btn-checkbox h100p' + (filter.additional.ada ? ' btn-checkbox-ada' : '')
                        }
                        onChange={() => onClickAccessibility()}
                        checked={filter.additional.ada}
                        htmlLabel={
                          <span>
                            <FontAwesomeIcon icon={faAccessibleIcon} />
                          </span>
                        }
                      />
                    }
                  />
                ) : (
                  <Tooltip
                    position="top"
                    content={t('accessibility')}
                    children={
                      <Checkbox
                        className={'btn-checkbox h100p cur-na'}
                        checked={false}
                        disabled={true}
                        htmlLabel={
                          <span>
                            <FontAwesomeIcon icon={faAccessibleIcon} />
                          </span>
                        }
                      />
                    }
                  />
                )}

                <Tooltip
                  position="top"
                  content={t('filter.free_cancellation')}
                  children={
                    <Checkbox
                      htmlLabel={
                        <span>
                          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-3" />
                          {t('filter.free_x')}
                        </span>
                      }
                      className={
                        'btn-checkbox h100p' +
                        (filter.additional.freeCxl ? ' btn-checkbox-free' : '')
                      }
                      onChange={() =>
                        props.onFilterChange({
                          ...filter,
                          additional: {
                            ...filter.additional,
                            freeCxl: !filter.additional.freeCxl,
                          },
                        })
                      }
                      checked={filter.additional.freeCxl}
                    />
                  }
                />
                {/* {!b2b && (
                  <Tooltip
                    position="top"
                    content={t('hotel.with_meal')}
                    children={
                      <Checkbox
                        className={
                          'btn-checkbox h100p' +
                          (filter.additional.withMeal ? ' btn-checkbox-meal' : '')
                        }
                        onChange={() =>
                          props.onFilterChange({
                            ...filter,
                            additional: {
                              ...filter.additional,
                              withMeal: !filter.additional.withMeal,
                            },
                          })
                        }
                        checked={filter.additional.withMeal}
                        htmlLabel={
                          <span>
                            <FontAwesomeIcon icon={faUtensils} />
                          </span>
                        }
                      />
                    }
                  />
                )} */}

                <Tooltip
                  position="top"
                  content={t('filter.preffered_hotels')}
                  children={
                    <Checkbox
                      htmlLabel={
                        <span>
                          <FontAwesomeIcon icon={faCrown} />
                        </span>
                      }
                      className={'btn-checkbox h100p  btn-checkbox-crown'}
                      onChange={() =>
                        props.onFilterChange({
                          ...filter,
                          additional: {
                            ...filter.additional,
                            preffered: !filter.additional.preffered,
                          },
                        })
                      }
                      checked={filter.additional.preffered}
                    />
                  }
                />

                {false && (
                  <Tooltip
                    position="top"
                    content={'Kosher'}
                    children={
                      <Checkbox
                        htmlLabel={
                          <span>
                            <FontAwesomeIcon icon={faK} />
                          </span>
                        }
                        disabled
                        className={'btn-checkbox h100p cur-na btn-checkbox-disabled'}
                      />
                    }
                  />
                )}

                {true && (
                  <div className="hotel-result-filter-input star">
                    <div className="star-wr">
                      {filter.meal.options?.map((option, index) => (
                        <Tooltip
                          content={t(filter.meal.tooltips?.[index] || '')}
                          key={index}
                          position="top"
                        >
                          <div
                            className={
                              'cur-poi flex-center h100p w100p' +
                              (filter.meal.value?.includes(option) ? ' active' : '') +
                              (!filter.meal.active.includes(option) ? ' disabled' : '')
                            }
                            key={index}
                            onClick={() =>
                              filter.meal.active.includes(option) &&
                              props.onFilterChange({
                                ...filter,
                                meal: {
                                  ...filter.meal,
                                  value: filter.meal.value.includes(option)
                                    ? filter.meal.value.filter((v) => v !== option)
                                    : [...filter.meal.value, option],
                                },
                              })
                            }
                          >
                            <span>{option}</span>
                          </div>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                )}
                {/* <FilterResultPrice
                  onFilterChange={props.onFilterChange}
                  filter={filter}
                  dynamic={!!props.dynamic}
                /> */}
              </Fragment>
            )}
            <FilterResultStars onFilterChange={props.onFilterChange} filter={filter} />
            <FilterResultLocation
              onLocationChange={props.onLocationChange}
              onFilterChange={props.onFilterChange}
              filter={filter}
            />
            <div className="ml-a"></div>
            {b2b && !props.dynamic && user?.branch?.hotelDisplaySupplierName && <FilterSupplier onFilterChange={props.onFilterChange} />}

            {b2b && !props.dynamic && !!user?.showNet && (
              <div className="hotel-result-filter-input position-relative" style={{ maxWidth: 77 }}>
                <SelectCustom
                  options={getPriceModesList(user)}
                  value={priceMode}
                  onChange={onChangePriceMode}
                  isDropdown
                />
              </div>
            )}

            {b2b && !props.dynamic && (
              <Tooltip content={t('mo.currency')} position="top">
                <div className="hotel-result-filter-input supp position-relative">
                  <SelectCustom
                    options={currencyState}
                    value={currency}
                    onChange={onChangeCurrency}
                    showOnlyIcon
                    isDropdown
                  />
                </div>
              </Tooltip>
            )}

            <div className="hotel-result-filter-input clear">
              <Tooltip
                content={t('filter.clear')}
                position={'top'}
                children={
                  <Button
                    title=""
                    icon={faFilterCircleXmark}
                    className={'btn btn-default h100p' + (hasFilterValue ? ' btn-active' : '')}
                    onClick={clearFilters}
                  />
                }
              />
            </div>
            {b2b && (
              <div className="hotel-result-filter-input clear">
                <Tooltip
                  content={t('search.with_active_filters')}
                  position={'top'}
                  children={
                    <button className={
                      'btn btn-default h100p' +
                      (filter.searchWithActiveFilters ? ' btn-active-blue' : '')
                    }
                      onClick={() =>
                        props.onFilterChange({
                          ...filter,
                          searchWithActiveFilters: !filter.searchWithActiveFilters,
                        })
                      }>
                      <span className='position-relative' style={{ left: 4 }}>
                        <FontAwesomeLayered icon1={faFilter} icon2={faLock} transform2='shrink-6 down-4 right-0' />

                      </span>

                    </button>
                  }
                />
              </div>
            )}
            <div className="show-laptop-sm w100p">
              <FilterResultLeft hotels={props.hotels} onChangeFilter={props.onFilterChange} />
            </div>
            <div className="w100p show-laptop-sm" style={{ order: 3 }}>
              <Button title={t('close')} className="btn btn-default" onClick={onClickClose} />
            </div>
          </div>
        </div>
      )}
      <div className="hotel-result-filter-row hotel-result-sort-by" data-assistant-target="sort">
        <div className="sort-by-buttons">
          <Button
            title={t('actions')}
            className="btn btn-primary show-laptop-sm"
            onClick={() => {
              const element = document.querySelector('.hotel-result-settings') as HTMLElement;
              element?.classList.toggle('visible-responsive');
              setInitedMap(true);
            }}
          />
          <Button
            title={t('filter.s')}
            className="btn btn-default show-laptop-sm"
            onClick={() => {
              const element = document.querySelector('.hotel-result-filter-row') as HTMLElement;
              element.classList.toggle('visible-responsive');
            }}
          />
          <Button
            title={t('sort.er')}
            className="btn btn-default show-laptop-sm"
            onClick={() => {
              const element = document.querySelector('.sort-responsive') as HTMLElement;
              element.classList.toggle('visible-responsive');
            }}
          />
        </div>
      </div>

      {!filter && <div className="hotel-result-filter-empty" />}
    </div>
  );
};

export default withErrorBoundary(FilterResults, 'FilterResults');
